/**
 * A quick look at a document without saving it.
 *
 * Checking that the right ID card is on file used to mean downloading it, finding it in
 * the downloads folder, opening it, and doing that again for the next person. PDFs and
 * photos open here, in the page; anything a browser cannot show (Word, Excel, a signed
 * .p7m) says so and offers the download instead of showing a blank box.
 *
 * Given the other files of the same folder it flips through them — ← and → too — which
 * is how a folder of course certificates actually gets reviewed.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/ui';
import { useDir, useI18n } from '@/i18n';
import { api, ApiError } from '@/lib/api';

export interface ViewableFile {
  id: string;
  filename: string;
  mimeType: string;
}

/** What the browser can draw itself. HEIC and TIFF are images browsers do not. */
function kindOf(file: ViewableFile): 'pdf' | 'image' | 'none' {
  const type = file.mimeType.toLowerCase();
  if (type === 'application/pdf' || /\.pdf$/i.test(file.filename)) return 'pdf';
  if (/^image\/(jpeg|png|webp|gif)$/.test(type)) return 'image';
  return 'none';
}

export function DocumentViewer({
  files,
  index,
  onIndex,
  onClose,
  onDownload,
}: {
  files: ViewableFile[];
  /** Which one is shown; null closes the viewer. */
  index: number | null;
  onIndex: (index: number) => void;
  onClose: () => void;
  onDownload: (id: string) => void;
}) {
  const { t } = useI18n();
  const { rtl, forward, back } = useDir();
  const file = index === null ? null : files[index];
  const kind = file ? kindOf(file) : 'none';

  const url = useQuery({
    queryKey: ['document-view', file?.id],
    queryFn: async () => (await api.viewUrl(file!.id)).url,
    enabled: Boolean(file) && kind !== 'none',
    // The signed link lasts five minutes; asking again after four keeps it working.
    staleTime: 4 * 60 * 1000,
    gcTime: 4 * 60 * 1000,
  });

  const [loaded, setLoaded] = useState(false);
  useEffect(() => setLoaded(false), [file?.id]);

  const hasPrev = index !== null && index > 0;
  const hasNext = index !== null && index < files.length - 1;

  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      // The arrow keys follow the screen, not the order of the files: in Arabic the
      // previous file is to the right.
      if (e.key === (rtl ? 'ArrowRight' : 'ArrowLeft') && hasPrev) onIndex(index - 1);
      if (e.key === (rtl ? 'ArrowLeft' : 'ArrowRight') && hasNext) onIndex(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, hasPrev, hasNext, onIndex, rtl]);

  return (
    <Modal
      open={file !== null}
      onClose={onClose}
      size="xl"
      title={file?.filename ?? ''}
      footer={
        file ? (
          <>
            <button type="button" className="btn-primary" onClick={() => onDownload(file.id)}>
              {t('common.download')}
            </button>
            {url.data ? (
              <a className="btn-secondary" href={url.data} target="_blank" rel="noopener noreferrer">
                {t('viewer.openTab')}
              </a>
            ) : null}
            {files.length > 1 ? (
              <div className="ms-auto flex items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={!hasPrev}
                  onClick={() => onIndex(index! - 1)}
                  aria-label={t('viewer.previous')}
                >
                  {back}
                </button>
                <span className="text-xs text-muted tabular">
                  {index! + 1} / {files.length}
                </span>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={!hasNext}
                  onClick={() => onIndex(index! + 1)}
                  aria-label={t('viewer.next')}
                >
                  {forward}
                </button>
              </div>
            ) : null}
          </>
        ) : null
      }
    >
      {!file ? null : kind === 'none' ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <p className="text-sm font-semibold">{t('viewer.noPreview')}</p>
          <p className="text-xs text-muted max-w-sm">{t('viewer.noPreviewHint')}</p>
          <button type="button" className="btn-primary mt-2" onClick={() => onDownload(file.id)}>
            {t('common.download')}
          </button>
        </div>
      ) : url.error ? (
        <p className="py-16 text-center text-sm text-danger">
          {url.error instanceof ApiError ? url.error.message : t('viewer.failed')}
        </p>
      ) : !url.data ? (
        <Spinner />
      ) : (
        <div className="relative h-[70vh] rounded border border-border bg-surface-alt overflow-hidden">
          {!loaded ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Spinner />
            </div>
          ) : null}
          {kind === 'pdf' ? (
            <iframe
              key={file.id}
              src={url.data}
              title={file.filename}
              className="w-full h-full"
              onLoad={() => setLoaded(true)}
            />
          ) : (
            <img
              key={file.id}
              src={url.data}
              alt={file.filename}
              className="w-full h-full object-contain"
              onLoad={() => setLoaded(true)}
            />
          )}
        </div>
      )}
    </Modal>
  );
}
